/**
 * 🧱 Refactor BT Phase 3 — hook « matrice de présence » des bons de travail (mode Éditer).
 *
 * Étape 1 : extraction MÉCANIQUE depuis EditerBonTravailDialog.jsx (HEAD Phase 2B) — chaque bloc est la
 * copie caractère pour caractère (indentation comprise) du code d'origine :
 *   A L124-129 `employesAyantPunche` · B L136-175 `membresExclus`/`membresExtras` · C L177-205
 *   `extrasRetiresManuellement`/`dialogSuppression` · H L831-849 `lockedInfo` + fetch locked-dates ·
 *   E L851-863 fetch employes-ayant-punche · D L421-462 `handleChefChange` · F L464-512
 *   `employesEffectifsIds`/`membresMatrice` · G L514-828 `toggleEmployeJour`, `getEmpDates`,
 *   `estVisibleDansListeEquipe`, `ouvrirDialogSuppression`, `fermerDialogSuppression`, `setChefDuJour` ·
 *   I L865-895 effet « réalité gravée ».
 * Seul changement : l'ordre de déclaration des hooks React (états déclarés ici plutôt qu'en tête du
 * composant) — sans effet observable (aucun code du composant ne lisait ces états entre les deux positions).
 * Refactor pur : comportements ET bugs (B-3, B-5…) préservés. `mode: 'creer'` : à venir — CreerBonTravailDialog
 * garde pour l'instant son implémentation propre (divergences documentées dans REFACTOR_BT_PHASE3.md).
 *
 * @param {object} p
 * @param {object} p.bon                BT en édition
 * @param {object} p.form / p.setForm   état du formulaire (déclaré par le composant)
 * @param {Array}  p.employees          employés FILTRÉS (`filterEmployeesForBT`)
 * @param {Array}  p.employesOriginal   employés non filtrés (détection installateur)
 * @param {object} p.dossier            dossier parent (sync chef d'équipe du dossier)
 * @param {Function} p.getMontantContrat
 */
import axios from 'axios';
import { useState, useEffect, useMemo, useRef } from 'react';
import { toast } from 'sonner';
import { API_URL, calculerHeuresBudgetees, calculerCoutEquipe, getCoutEmployeur } from '../components/crm/bon-travail/utils';
import { estVisibleDansListeEquipe as estVisibleDansListeEquipePure } from '../components/crm/bon-travail/equipeHelpers';

export function useBtEquipeMatrice({ mode = 'editer', bon, form, setForm, employees, employesOriginal, dossier, getMontantContrat }) {
  if (mode !== 'editer') throw new Error(`useBtEquipeMatrice : mode « ${mode} » non pris en charge (Étape 1 : Éditer seulement)`);
  // 🐛 [Phase v13.46 — 2026-07-14] Employés ayant réellement punché sur ce BT.
  //   Remplace le filtre statique `e.chef_equipe_id === form.chef_equipe_id`
  //   cassé post-Phase 107.F (chef_equipe_id soft-deleted à null → filtres
  //   retournaient TOUJOURS des listes vides → employés fantômes invisibles).
  //   Alimenté par GET /api/crm/bons-travail/{id}/employes-ayant-punche.
  const [employesAyantPunche, setEmployesAyantPunche] = useState([]);

  const [membresExclus, setMembresExclus] = useState(() => {
    // 🚀 [Phase 107.B — 2026-07-14] Fin des équipes prédéterminées :
    //   plus de "membres implicites d'équipe" via chef_equipe_id. Toute la
    //   liste `employes_assignes` sauvegardée est traitée comme des ajouts
    //   manuels → aucun défaut à exclure.
    return new Set();
  });
  // 🧱 Phase 4 — E-3 (B-24) : les assignés RÉELS du BT (`bon.employes_assignes`) alimentent `membresExtras`.
  //   Avant : calcul une seule fois au mount (`useState(() => …)`) → `[]` si `employees` (props async du
  //   parent) n'était pas encore chargé → matrice = chef seul (B-24). Maintenant : même calcul, exécuté
  //   quand `employees` arrive (effet ci-dessous, une seule fois, garde `extrasInitialisesRef`).
  //   Jamais de complétion depuis la fiche employé (`employees.chef_equipe_id`, équipe fixe abolie — Lot E).
  //   Un assigné relu avec `dates_assignees: []` (aucun jour) reste affiché avec 0 case cochée :
  //   l'utilisatrice voit qu'il est « retiré de tous les jours » et peut le recocher.
  const extrasDepuisBon = () => {
    // 🚀 [Phase 107.B — 2026-07-14] Tous les employes_assignes du BT sont
    //   traités comme "extras" (sélection manuelle par l'utilisatrice) sauf
    //   ceux qui sont explicitement marqués role='chef_equipe' (chef principal).
    // Cat H — `a.role` = rôle DANS L'ÉQUIPE DU BT (chef vs membre), pas rôle utilisateur RBAC.
    // 🚫 [v13.65.5 — 2026-02-28] Filtre INSTALLATEUR : les installateurs ne
    //   doivent JAMAIS apparaître sur un BT (règle métier utilisatrice). On
    //   les exclut ici pour qu'ils n'apparaissent PAS dans la matrice
    //   « Présence par jour » ni dans la liste « Membres de l'équipe » —
    //   même si un BT legacy en contient encore. Le backend applique le
    //   même filtre au save (défense en profondeur).
    //   Détection : role='installateur' explicite OU employé (unfiltered)
    //   avec role_terrain/type_employe='installateur'.
    //   Ref : /app/memory/REGLES_PLATEFORME.md § Règle 3.
    if (!employees.length) return [];
    const isInstallateurEmp = (id) => {
      const emp = (employesOriginal || []).find(e => e?.id === id);
      if (!emp) return false;
      const rt = String(emp.role_terrain || '').toLowerCase().trim();
      const te = String(emp.type_employe || '').toLowerCase().trim();
      return rt === 'installateur' || te === 'installateur';
    };
    return (bon.employes_assignes || [])
      .filter(a => a.role !== 'chef_equipe' && a.employee_id !== bon.chef_equipe_id)  // E3b-whitelist : rôle d'assignation sur le BT (donnée)
      .filter(a => {
        const role = String(a?.role || '').toLowerCase().trim();
        return role !== 'installateur' && !isInstallateurEmp(a?.employee_id);  // E3b-whitelist : rôle d'assignation sur le BT (donnée)
      })
      .map(a => {
        const emp = employees.find(e => e.id === a.employee_id);
        return emp || { id: a.employee_id, prenom: (a.nom || '').split(' ')[0] || '', nom: (a.nom || '').split(' ').slice(1).join(' ') || '' };
      });
  };
  const [membresExtras, setMembresExtras] = useState(extrasDepuisBon);
  const extrasInitialisesRef = useRef(employees.length > 0);
  useEffect(() => {
    if (extrasInitialisesRef.current || !employees.length) return;
    extrasInitialisesRef.current = true;
    const depuisBon = extrasDepuisBon();
    if (!depuisBon.length) return;
    setMembresExtras(prev => {
      const deja = new Set(prev.map(e => e.id));
      return [...prev, ...depuisBon.filter(e => !deja.has(e.id))];
    });
  }, [employees.length]);  // eslint-disable-line react-hooks/exhaustive-deps

  // 🆕 2026-05-18 — Mémoire des employés retirés manuellement de "Membres de l'équipe"
  // (chip violet × cliqué). Empêche l'useEffect "réalité gravée" et setChefDuJour de
  // les ré-injecter automatiquement après suppression explicite.
  // 🛡️ [Phase 57 — v11 — 2026-06-14] Mémoire DB des employés retirés explicitement
  // (chip × cliqué OU peintre décoché de la matrice présence). Initialisé depuis
  // `bon.employes_extras_retires_explicitement` (champ persisté en DB). Cette
  // liste empêche le useEffect L503-527 « réalité gravée » de ré-injecter des
  // employés que l'utilisatrice a expressément retirés au save précédent.
  // Réf : /app/memory/audit_auto_injection_employes_phase57.md
  const [extrasRetiresManuellement, setExtrasRetiresManuellement] = useState(
    () => new Set(bon.employes_extras_retires_explicitement || [])
  );

  // 🛡️ [Phase 61 — 2026-06-17] Source unique de vérité « matrice » :
  // Dialog de confirmation quand l'utilisatrice clique sur la croix × d'un
  // employé qui a encore ≥ 1 jour coché dans Présence par jour. Empêche les
  // suppressions accidentelles + garantit la cohérence liste ↔ matrice
  // (décocher toutes les cases vide aussi la chip).
  // Réf : sémantique Phase 61 — règle 1 (avertir, pas bloquer dur).
  const [dialogSuppression, setDialogSuppression] = useState({
    open: false,
    type: null, // 'officiel' | 'extra'
    employeeId: null,
    nom: '',
    joursCoches: [],
    joursPunches: [],  // 🆕 [Bug prod 2026-07-16] Sous-ensemble des jours avec punch (protection Phase 61)
    aPunche: false,    // 🆕 True si l'employé a ≥1 punch — modifie titre/message/bouton
    onConfirm: null,
  });

  // 🧱 Refactor BT Phase 3 — I-1 (ordre HEAD strict) : effet « budget auto » (HEAD L382-424) replacé ici, AVANT les effets
  //   punchés / verrous / réalité gravée (HEAD L845/860/875), exactement comme à HEAD. Corps verbatim.
  useEffect(() => {
    if (form.heures_budgetees_ajustees_manuellement) return;
    if (!form.chef_equipe_id || !employees?.length) return;
    const montant = getMontantContrat();
    if (montant <= 0) return;
    const dateRef = form.dates_chantier?.[0];
    // 🚀 [Phase 107.B — 2026-07-14] Fin des équipes prédéterminées :
    //   les "membres implicites" ne sont plus filtrés par chef_equipe_id.
    //   On considère TOUS les employés terrain actifs (excluant sous-traitants,
    //   installateurs, spécialistes lavage) MOINS le chef principal (déjà compté)
    //   et les extras (ajoutés/exclus manuellement).
    const ROLES_EXCLUS_MO = ['sous_traitant', 'installateur'];
    const isTerrain = (e) => {
      const rt = String(e.role_terrain || '').toLowerCase();
      const te = String(e.type_employe || '').toLowerCase();
      if (ROLES_EXCLUS_MO.includes(rt) || ROLES_EXCLUS_MO.includes(te)) return false;
      return rt === 'peintre' || rt === 'chef_equipe' || te === 'peintre' || te === 'chef_equipe';
    };
    const membres = employees.filter(e =>
      isTerrain(e)
      && e.est_actif !== false
     
      && e.id !== form.chef_equipe_id
      && !membresExclus.has(e.id)
    );
    const chef = employees.find(e => e.id === form.chef_equipe_id);
    const tousLesEmployes = [];
    if (chef) tousLesEmployes.push(chef);
    tousLesEmployes.push(...membres);
    tousLesEmployes.push(...membresExtras);
    const nbMembres = tousLesEmployes.length;
    if (nbMembres === 0) return;
    let coutTotal = 0;
    for (const m of tousLesEmployes) coutTotal += getCoutEmployeur(m, dateRef);
    const coutMoyen = coutTotal > 0 ? coutTotal / nbMembres : 0;
    if (coutMoyen <= 0) return;
    const calc = calculerHeuresBudgetees(montant, coutMoyen, nbMembres, form.dates_chantier);
    if (calc.heures > 0) {
      const nouvelleValeur = calc.heures.toString();
      setForm(f => (f.heures_budgetees === nouvelleValeur ? f : { ...f, heures_budgetees: nouvelleValeur }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.chef_equipe_id, JSON.stringify(Array.from(membresExclus)), JSON.stringify(membresExtras.map(e => e.id)), JSON.stringify(form.dates_chantier), form.heures_budgetees_ajustees_manuellement, employees]);

  // 🔒 2026-05-14 — Verrou des journées avec heures enregistrées
  //   { locked_dates, locked_per_employee, absent_per_employee, settled_dates }
  //   - locked_per_employee[emp_id] = dates où l'employé a punché (forcer ✓ verrouillé)
  //   - absent_per_employee[emp_id] = dates passées où l'employé NE s'est PAS
  //     punché (forcer — verrouillé). La réalité grave les présences ET les
  //     absences pour les jours passés du BT.
  const [lockedInfo, setLockedInfo] = useState({
    locked_dates: [], locked_per_employee: {}, absent_per_employee: {}, settled_dates: []
  });
  useEffect(() => {
    if (!bon?.id) return;
    axios.get(`${API_URL}/api/crm/bons-travail/${bon.id}/locked-dates`)
      .then(r => setLockedInfo(r.data || {
        locked_dates: [], locked_per_employee: {}, absent_per_employee: {}, settled_dates: []
      }))
      .catch(() => setLockedInfo({
        locked_dates: [], locked_per_employee: {}, absent_per_employee: {}, settled_dates: []
      }));
  }, [bon?.id]);

  // 🐛 [Phase v13.46 — 2026-07-14] Fetch des employés qui ont réellement punché
  //   sur ce BT. Alimente `employesAyantPunche` utilisé pour construire
  //   `employesEffectifs` (source de vérité unique remplaçant l'ancien filtre
  //   `e.chef_equipe_id === form.chef_equipe_id` cassé post-Phase 107.F).
  useEffect(() => {
    if (!bon?.id) {
      setEmployesAyantPunche([]);
      return;
    }
    axios.get(`${API_URL}/api/crm/bons-travail/${bon.id}/employes-ayant-punche`)
      .then(r => setEmployesAyantPunche(r.data?.employes || []))
      .catch(() => setEmployesAyantPunche([]));
  }, [bon?.id]);

  const handleChefChange = (empId) => {
    // 🆕 Sentinelle « __none__ » pour l'option « — Aucun — » du dropdown
    if (empId === '__none__') {
      // 🆕 [Phase 91 — 2026-07-07] Nettoyage complet quand chef retiré :
      //   1. Reset chef_equipe_id / nom
      //   2. Reset chef_dates_assignees = [] (empêche dates orphelines)
      //   3. Force role='extra' sur tous les form.employes_assignes (payload cohérent)
      //   4. Convertit l'ancien chef + membres en membresExtras pour affichage UI
      const anciensMembresIds = new Set([
        ...(form.chef_equipe_id ? [form.chef_equipe_id] : []),
        ...((form.employes_assignes || []).map(e => e.employee_id).filter(Boolean)),
        ...membresExtras.map(m => m.id),
      ]);
      const nouveauxExtras = employees.filter(e => anciensMembresIds.has(e.id));
      setForm(f => ({
        ...f,
        chef_equipe_id: '',
        chef_equipe_nom: '',
        chef_dates_assignees: [],
        employes_assignes: (f.employes_assignes || []).map(ea => ({ ...ea, role: 'extra' })),
      }));
      setMembresExtras(nouveauxExtras);
      setMembresExclus(new Set());
      return;
    }
    const emp = employees.find(e => e.id === empId);
    const chefNom = emp ? `${emp.prenom} ${emp.nom}` : '';
    
    // Calculer les heures automatiquement
    const montant = getMontantContrat();
    const equipe = calculerCoutEquipe(empId, employees, form.dates_chantier?.[0]);
    const calc = calculerHeuresBudgetees(montant, equipe.coutMoyen, equipe.nbMembres, form.dates_chantier);
    const newHeures = calc.heures > 0 ? calc.heures.toString() : form.heures_budgetees;
    
    setForm(f => ({ ...f, chef_equipe_id: empId, chef_equipe_nom: chefNom, heures_budgetees: newHeures }));
    setMembresExclus(new Set()); // Reset exclusions quand on change de chef
    
    // Mettre à jour le chef d'équipe dans le dossier client
    if (dossier?.id && empId) {
      axios.put(`${API_URL}/api/crm/dossiers/${dossier.id}`, { ...dossier, chef_equipe_id: empId, chef_equipe_nom: chefNom }).catch(() => {});
    }
  };

  // 🐛 [Phase v13.46 — 2026-07-14] Source de vérité unique remplaçant
  //   l'ancien filtre statique `e.chef_equipe_id === form.chef_equipe_id`
  //   cassé post-Phase 107.F. Set des IDs des employés considérés "affectés"
  //   à ce BT selon la nouvelle logique dynamique :
  //     - Le chef sélectionné (s'il y en a un)
  //     - Tout employé qui a réellement punché sur le BT (via API)
  //     - Tout employé explicitement présent dans `bon.employes_assignes`
  //       (ajouts manuels + affectations sauvegardées, filtre extras hérité)
  // 🚀 [Phase v13.46.4 — 2026-07-17] REFONTE — Retrait des vestiges bon.employes_assignes
  // Doctrine : la matrice ne contient QUE les employés « légitimes » :
  //     - chef principal (form.chef_equipe_id)
  //     - employés qui ont RÉELLEMENT punché (employesAyantPunche)
  //     - employés ajoutés MANUELLEMENT dans la session (membresExtras)
  // Les vestiges Phase 107 (bon.employes_assignes hérité) sont IGNORÉS pour ne pas
  // polluer l'affichage sur les BT existants pré-Phase 107 avec 15 assignés fantômes.
  const employesEffectifsIds = useMemo(() => {
    const ids = new Set();
    if (form.chef_equipe_id) ids.add(form.chef_equipe_id);
    (employesAyantPunche || []).forEach(p => p.employee_id && ids.add(p.employee_id));
    (membresExtras || []).forEach(e => e.id && ids.add(e.id));
    return ids;
  }, [form.chef_equipe_id, employesAyantPunche, membresExtras]);

  // 🆕 2026-04-30 — Liste effective des membres pour la matrice de présence
  // (chef + membres pas exclus + extras), enrichis avec dates_assignees lus depuis bon.employes_assignes
  //
  // 🆕 [Phase v13.46.4 — 2026-07-17] Retrait du fallback bon.employes_assignes.
  //   Doctrine finale : la matrice contient UNIQUEMENT :
  //     - le chef sélectionné (si applicable)
  //     - les employés qui ont RÉELLEMENT punché (employesAyantPunche)
  //     - les employés ajoutés MANUELLEMENT dans la session (membresExtras)
  //   Les vestiges de bon.employes_assignes (Phase 107) sont EXCLUS.
  const membresMatrice = useMemo(() => {
    if (!employees?.length && !form.chef_equipe_id && !employesAyantPunche.length) return [];
    const liste = [];
    if (form.chef_equipe_id) {
      const chef = employees.find(e => e.id === form.chef_equipe_id);
      if (chef) liste.push({ employee_id: chef.id, employee_nom: `${chef.prenom} ${chef.nom}`, est_chef: true });
    }
    employees
      .filter(e => employesEffectifsIds.has(e.id) && e.id !== form.chef_equipe_id && e.est_actif !== false && !membresExclus.has(e.id))
      .forEach(e => liste.push({ employee_id: e.id, employee_nom: `${e.prenom} ${e.nom}`, est_chef: false }));
    membresExtras.forEach(e => {
      if (!liste.find(m => m.employee_id === e.id)) {
        liste.push({ employee_id: e.id, employee_nom: `${e.prenom} ${e.nom}`, est_chef: false });
      }
    });
    return liste;
  }, [form.chef_equipe_id, employees, membresExclus, membresExtras, employesEffectifsIds, employesAyantPunche]);

  // 🆕 Toggle présence d'un employé sur un jour spécifique
  // 🐛 2026-05-14 — Refactor : utilise setForm fonctionnel (callback `f =>`) pour
  //   éviter toute lecture de state périmé. Évite le bug où des clics rapides
  //   pouvaient écraser les modifications précédentes.
  // 🔒 2026-05-14 — Bloque les dates verrouillées (heures enregistrées) ET
  //   les dates « absent verrouillé » (jour passé du BT avec d'autres punches
  //   mais l'employé ne s'est pas punché — la réalité grave son absence).
  const toggleEmployeJour = (employee_id, date, est_chef) => {
    const empLocked = (lockedInfo?.locked_per_employee || {})[employee_id] || [];
    const empAbsent = (lockedInfo?.absent_per_employee || {})[employee_id] || [];
    if (empLocked.includes(date)) {
      toast.error(
        "🔒 Journée verrouillée : des heures ont déjà été enregistrées pour cet employé à cette date.",
        { duration: 4000 }
      );
      return;
    }
    if (empAbsent.includes(date)) {
      toast.error(
        "🔒 Absence gravée : l'employé ne s'est pas punché ce jour-là et la journée est déjà validée.",
        { duration: 4500 }
      );
      return;
    }
    const empNom = membresMatrice.find(m => m.employee_id === employee_id)?.employee_nom || '';
    if (est_chef) {
      setForm(f => {
        const dc = f.dates_chantier || [];
        // 🛡️ [Phase 58 — 2026-06-14] PRÉSERVE [] explicite (intention utilisatrice).
        // Si chef_dates_assignees = [] (chef explicitement absent partout), NE PAS faire
        // fallback vers dc : sinon le click suivant ressusciterait le chef.
        // Réf : /app/memory/audit_auto_injection_presence_par_jour_phase58.md
        const cdaArr = Array.isArray(f.chef_dates_assignees) ? f.chef_dates_assignees : dc;
        const has = cdaArr.includes(date);
        const next = has ? cdaArr.filter(d => d !== date) : [...cdaArr, date].sort();

        // 🚀 [Phase v13.46.7 — 2026-07-18] Synchro matrice → dropdown « Chef du jour ».
        // Bug prod : quand l'utilisatrice décochait le chef principal dans la matrice
        // pour date X, `chefs_par_jour[X]` restait vide → le dropdown retombait
        // sur `chef_equipe_id` au reload → visuellement, le chef principal
        // reforcé sur le calendrier de production ce jour-là.
        //
        // Fix : quand on RETIRE une date du chef principal (`has=true`) et que
        // `chefs_par_jour[X]` n'a pas déjà un chef alternatif explicite, on
        // force `chefs_par_jour[X] = '__none__'` (Sans chef ce jour).
        //
        // Symétrie inverse : quand on COCHE la date du chef principal
        // (`has=false`), on retire l'entrée `chefs_par_jour[X]` s'il pointait
        // sur `'__none__'` ou sur le chef principal lui-même (retour au fallback
        // implicite qui est CE chef principal).
        const newChefs = { ...(f.chefs_par_jour || {}) };
        const chefPrincipal = f.chef_equipe_id;
        if (has) {
          // Décochage : forcer '__none__' UNIQUEMENT si aucun chef alternatif
          //   déjà défini pour cette date. Un chef alternatif reste prioritaire.
          const cpjExisting = newChefs[date];
          if (!cpjExisting || cpjExisting === chefPrincipal) {
            newChefs[date] = '__none__';
          }
        } else {
          // Cochage : retour au chef principal — nettoyer `'__none__'` ou une
          //   entrée pointant sur le chef principal (redondante).
          const cpjExisting = newChefs[date];
          if (cpjExisting === '__none__' || cpjExisting === chefPrincipal) {
            delete newChefs[date];
          }
        }
        return { ...f, chef_dates_assignees: next, chefs_par_jour: newChefs };
      });
    } else {
      setForm(f => {
        const dc = f.dates_chantier || [];
        const list = [...(f.employes_assignes || [])];
        const idx = list.findIndex(e => e.employee_id === employee_id);
        if (idx === -1) {
          // 🚀 [Phase v13.46.5 — 2026-07-18] FIX Bug auto-cochage.
          //   Bug prod : quand l'utilisatrice cochait UNE seule journée pour un
          //   employé jamais coché avant (via dropdown notamment), le système
          //   utilisait `baseDates = dc` (toutes les dates du chantier). Le clic
          //   sur la 1ère date la RETIRAIT (car déjà présente implicitement),
          //   résultat : 3 autres dates cochées automatiquement au lieu d'1.
          //
          //   Sémantique corrigée : chaque case est cochée MANUELLEMENT et
          //   INDIVIDUELLEMENT. La 1ère interaction démarre depuis un set VIDE
          //   → seule la date cliquée devient assignée.
          //
          //   Chef (branche `est_chef` ci-dessus) conserve son comportement
          //   séparé via `chef_dates_assignees`. Phase 61 verrouillage intact.
          const next = [date];
          list.push({ employee_id, employee_nom: empNom, est_chef: false, dates_assignees: next });
        } else {
          const existing = list[idx].dates_assignees;
          // 🛡️ [Phase 58 — 2026-06-14] PRÉSERVE [] explicite (intention utilisatrice).
          // CAS RACINE bug Carolane Gagnon : si existing = [] (employé déjà décoché
          // partout par l'utilisatrice), le pattern précédent `length>0 ? existing : dc`
          // faisait fallback vers dc → next = dc.filter(d => d !== date) → employé
          // ressuscité sur (dates_chantier - 1). NE JAMAIS écraser [] explicite.
          const baseArr = Array.isArray(existing) ? existing : dc;
          const has = baseArr.includes(date);
          const next = has ? baseArr.filter(d => d !== date) : [...baseArr, date].sort();
          list[idx] = { ...list[idx], dates_assignees: next };
        }
        return { ...f, employes_assignes: list };
      });
    }
  };

  const getEmpDates = (employee_id, est_chef) => {
    if (est_chef) {
      // 🛡️ [Phase 58] PRÉSERVE [] explicite (chef volontairement absent partout).
      return Array.isArray(form.chef_dates_assignees)
        ? form.chef_dates_assignees
        : (form.dates_chantier || []);
    }
    const found = (form.employes_assignes || []).find(e => e.employee_id === employee_id);
    if (found?.dates_assignees && found.dates_assignees.length > 0) {
      return found.dates_assignees;
    }
    if (found) {
      // 🆕 2026-05-18 — Distinction critique :
      //   - `dates_assignees` est un ARRAY VIDE explicite [] → l'utilisateur a
      //     volontairement retiré TOUTES les dates → l'employé n'est PRÉSENT
      //     AUCUN jour. Ne PAS faire de fallback vers dc !
      //   - `dates_assignees` est absent/undefined → entrée legacy sans dates
      //     explicites → fallback historique sur toutes les dates du chantier.
      if (Array.isArray(found.dates_assignees)) {
        // Array vide explicite → décochage intentionnel total → retourner []
        return [];
      }
      // Présent dans employes_assignes mais sans dates_assignees défini → fallback historique
      return form.dates_chantier;
    }
    // 🆕 2026-05-14 — Membre auto-injecté (a punché mais pas dans l'équipe officielle).
    //   Ne cocher QUE les dates où il a effectivement punché. Évite d'afficher
    //   ✓ vert sur des jours passés où la personne n'était PAS là.
    const punchedDates = (lockedInfo?.locked_per_employee || {})[employee_id] || [];
    if (punchedDates.length > 0) return punchedDates;
    // Aucun punch ni assignation → liste vide (toutes les cases décochées)
    return [];
  };

  // 🛡️ [Phase 61 — 2026-06-17] Source unique de vérité « matrice » :
  // Wrapper qui appelle le helper pur exporté depuis `equipeHelpers.js`
  // (testé par 15 tests Jest Phase 61). Garantit qu'une chip ne s'affiche
  // dans la liste à gauche QUE si l'employé a ≥ 1 jour coché OU s'il n'a
  // pas encore d'entrée (implicite par défaut).
  const estVisibleDansListeEquipe = (employee_id) =>
    estVisibleDansListeEquipePure(employee_id, form.employes_assignes);

  // 🛡️ [Phase 61 — 2026-06-17] Ouvre le dialog d'avertissement avant retrait
  // d'un employé qui a encore ≥ 1 jour coché. Si 0 jour coché, exécute
  // directement onConfirm sans dialog (suppression silencieuse).
  //
  // 🆕 [Bug prod 2026-07-16] Détecte les employés ayant PUNCHÉ pour afficher
  // un message différent (Phase 61 doctrine : ces employés ne peuvent PAS être
  // retirés — le backend auto-préserve leurs dates verrouillées). Le dialog
  // devient informatif : "OK je désactive uniquement Présence par jour".
  const ouvrirDialogSuppression = (type, employeeId, nom, onConfirm) => {
    const joursCoches = getEmpDates(employeeId, false) || [];
    const joursPunches = (lockedInfo?.locked_per_employee || {})[employeeId] || [];
    const aPunche = joursPunches.length > 0;

    if (joursCoches.length === 0 && !aPunche) {
      // Pas de cases cochées ET jamais punché → suppression directe sans dialog
      onConfirm();
      return;
    }
    setDialogSuppression({
      open: true,
      type,
      employeeId,
      nom,
      joursCoches: [...joursCoches].sort(),
      joursPunches: [...joursPunches].sort(),
      aPunche,
      onConfirm,
    });
  };

  const fermerDialogSuppression = () => {
    setDialogSuppression({
      open: false, type: null, employeeId: null, nom: '',
      joursCoches: [], joursPunches: [], aPunche: false, onConfirm: null,
    });
  };

  // 🆕 2026-05 : sélectionne le chef d'équipe pour UN jour précis (chefs_par_jour)
  // Auto-ajoute le chef sélectionné à employes_assignes pour ce jour si absent,
  // et ajoute aussi ses peintres terrain si pas déjà présents.
  // 🆕 2026-05-08 — Sentinelle "__none__" pour différencier "Aucun explicite" (pas de fallback)
  // de "non défini" (fallback vers chef principal).
  const setChefDuJour = (dateStr, empId) => {
    setForm(f => {
      const newChefs = { ...(f.chefs_par_jour || {}) };
      const chefPrincipal = f.chef_equipe_id;
      const dc = f.dates_chantier || [];

      // 🚀 [Phase v13.46.7 — 2026-07-18] Synchro dropdown → matrice.
      // Bug prod : sélectionner « Aucun » (ou un autre chef) ne décochait pas
      // le chef principal dans la matrice « Présence par jour ». L'utilisatrice
      // devait faire 2 actions manuelles ; incohérence si elle en oubliait une.
      //
      // Sémantique symétrique (miroir de la synchro matrice → dropdown P1) :
      //   - Sélectionner un chef alternatif Y (empId=Y ≠ '__none__' ≠ chef principal)
      //       → retire dateStr de `chef_dates_assignees` du chef principal
      //       → ajoute dateStr à `chef_dates_assignees` de Y (via employes_assignes)
      //   - Sélectionner « Aucun » (empId='__none__')
      //       → retire dateStr de `chef_dates_assignees` du chef principal
      //       → ne coche personne (jour Sans chef)
      //   - Sélectionner le chef principal explicitement (empId=chefPrincipal)
      //       → ajoute dateStr à `chef_dates_assignees` du chef principal
      //       → nettoie l'entrée `chefs_par_jour[dateStr]` (retour au fallback)
      //
      // Protection Phase 61 : si le chef principal a punché dateStr, on
      // NE le décoche PAS (la case reste verrouillée cursor-not-allowed). La
      // vérif se fait via `lockedInfo.locked_per_employee` — la matrice
      // rejettera visuellement toute manipulation, on est safe.
      const cdaCurrent = Array.isArray(f.chef_dates_assignees)
        ? f.chef_dates_assignees
        : dc;
      const chefPunchedLocked = chefPrincipal
        ? ((lockedInfo?.locked_per_employee || {})[chefPrincipal] || []).includes(dateStr)
        : false;

      const removeDateFromChefPrincipal = (arr) => {
        if (!Array.isArray(arr)) return arr;
        if (chefPunchedLocked) return arr;   // Phase 61 : ne pas décocher
        return arr.filter(d => d !== dateStr);
      };
      const addDateToChefPrincipal = (arr) => {
        const base = Array.isArray(arr) ? arr : dc;
        return base.includes(dateStr) ? base : [...base, dateStr].sort();
      };

      if (empId === '__none__') {
        // Aucun chef ce jour-là, explicite
        newChefs[dateStr] = '__none__';
        return {
          ...f,
          chefs_par_jour: newChefs,
          chef_dates_assignees: removeDateFromChefPrincipal(cdaCurrent),
        };
      }
      if (!empId) {
        // Reset (jamais utilisé via dropdown, mais safe pour appelants externes)
        delete newChefs[dateStr];
        return { ...f, chefs_par_jour: newChefs };
      }

      // empId = chef principal ou chef alternatif
      newChefs[dateStr] = empId;

      // Cas spécial : sélection du chef principal explicitement
      //   → nettoyer l'entrée `chefs_par_jour` (retour au fallback implicite)
      //   → cocher le chef principal dans la matrice
      if (empId === chefPrincipal) {
        delete newChefs[dateStr];
        return {
          ...f,
          chefs_par_jour: newChefs,
          chef_dates_assignees: addDateToChefPrincipal(cdaCurrent),
        };
      }

      // Cas standard : chef alternatif Y
      //   → retirer dateStr du chef principal (sauf si punché — Phase 61)
      //   → ajouter Y à `employes_assignes` avec `dates_assignees` incluant dateStr
      //   → ajouter les peintres de Y (auto-injection historique conservée)
      let newAssignes = [...(f.employes_assignes || [])];
      const chefEmp = employees?.find(e => e.id === empId);
      if (chefEmp) {
        const chefIdx = newAssignes.findIndex(e => e.employee_id === empId);
        if (chefIdx === -1) {
          newAssignes.push({
            employee_id: empId,
            employee_nom: `${chefEmp.prenom} ${chefEmp.nom}`,
            dates_assignees: [dateStr],
            est_chef: true,
          });
        } else {
          const cur = newAssignes[chefIdx].dates_assignees || [];
          if (!cur.includes(dateStr)) {
            newAssignes[chefIdx] = { ...newAssignes[chefIdx], dates_assignees: [...cur, dateStr].sort() };
          }
        }
        // 🧱 Phase 4 — Lot E (équipe fixe abolie) : le choix d'un chef du jour n'injecte plus « ses » peintres (fiche).
      }
      return {
        ...f,
        chefs_par_jour: newChefs,
        employes_assignes: newAssignes,
        chef_dates_assignees: removeDateFromChefPrincipal(cdaCurrent),
      };
    });
  };

  // 🆕 2026-05-14 — RÉALITÉ GRAVÉE : forcer les employés qui ont punché à apparaître
  //   dans "Membres de l'équipe" même s'ils ne font pas partie de l'équipe officielle.
  //   Exemple : Greg = chef, mais Audrey + Jacob (équipe Jean-Fra) ont aussi punché
  //   → ils sont auto-injectés dans `membresExtras` pour qu'on voie leurs jours
  //   présents dans la matrice "Présence par jour" (avec les cases verrouillées vertes 🔒).
  useEffect(() => {
    const punchedEmpIds = Object.keys(lockedInfo?.locked_per_employee || {});
    if (!punchedEmpIds.length || !employees.length) return;
    setMembresExtras(prev => {
      // IDs déjà couverts : chef principal + membres effectifs (v13.46) + extras actuels
      const officialIds = new Set([
        form.chef_equipe_id,
        // 🐛 [Phase v13.46 — 2026-07-14] Utilise employesEffectifsIds
        //   (union chef+punchs+assignations) au lieu de `e.chef_equipe_id === form.chef_equipe_id`
        //   soft-deleted post-Phase 107.F.
        ...Array.from(employesEffectifsIds),
        ...prev.map(m => m.id),
      ].filter(Boolean));
      const toAdd = [];
      for (const empId of punchedEmpIds) {
        if (officialIds.has(empId)) continue;
        // 🆕 2026-05-18 — Respecter les retraits manuels (chip × cliqué).
        // Si l'utilisateur a explicitement retiré l'employé, ne pas le ré-injecter.
        if (extrasRetiresManuellement.has(empId)) continue;
        const emp = employees.find(e => e.id === empId);
        if (emp) toAdd.push(emp);
      }
      if (!toAdd.length) return prev;
      return [...prev, ...toAdd];
    });
  }, [lockedInfo, employees, form.chef_equipe_id, extrasRetiresManuellement]);

  return {
    employesAyantPunche, setEmployesAyantPunche,
    membresExclus, setMembresExclus,
    membresExtras, setMembresExtras,
    extrasRetiresManuellement, setExtrasRetiresManuellement,
    dialogSuppression, setDialogSuppression,
    lockedInfo, setLockedInfo,
    handleChefChange,
    employesEffectifsIds, membresMatrice,
    toggleEmployeJour, getEmpDates, estVisibleDansListeEquipe,
    ouvrirDialogSuppression, fermerDialogSuppression, setChefDuJour,
  };
}
